import React, { useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  CardHeader,
  Collapse,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input
} from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import classnames from "classnames";
import { Link, useParams } from "react-router-dom";

import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { productDetails as defaultProductDetails } from "../../../common/data/ecommerce";
import EcommerceOrderProduct from "./EcommerceOrderProduct";
import avatar3 from "../../../assets/images/users/avatar-3.jpg";
import { getOrder, updateOrder } from "../../../api/ecommerce";
import * as moment from "moment";

const EcommerceOrderDetail = (props) => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const [shipmentForm, setShipmentForm] = useState({
    courier_company: "",
    tracking_number: "",
    tracking_url: "",
    shipment_date: "",
    estimated_delivery_date: "",
    notifyCustomer: false
  });

  const toggleModal = () => setModal(!modal);

  const handleEditShipment = () => {
    setShipmentForm({
      courier_company: order?.courier_company || "",
      tracking_number: order?.tracking_number || "",
      tracking_url: order?.tracking_url || "",
      shipment_date: order?.shipment_date ? moment(order.shipment_date).format("YYYY-MM-DD") : "",
      estimated_delivery_date: order?.estimated_delivery_date ? moment(order.estimated_delivery_date).format("YYYY-MM-DD") : "",
      notifyCustomer: false
    });
    toggleModal();
  };

  const handleShipmentSubmit = async (e) => {
    e.preventDefault();
    if (!shipmentForm.courier_company || !shipmentForm.tracking_number) {
      toast.error("Courier Company and Tracking Number are required!");
      return;
    }
    try {
      setLoading(true);
      await updateOrder(order.order_number, shipmentForm);
      toast.success("Shipment information updated successfully!");
      toggleModal();
      // Refetch order
      const data = await getOrder(id);
      setOrder(data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to update shipment info.");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (id) {
      setLoading(true);
      getOrder(id).then(data => {
        setOrder(data);
      }).catch(err => {
        console.error(err);
      }).finally(() => setLoading(false));
    }
  }, [id]);

  const getOrderProgress = (status) => {
    const s = (status || "pending").toLowerCase();
    const isCancelled = s === "cancelled" || s === "returns";
    const levels = {
      "pending": 2,
      "inprogress": 3,
      "pickups": 4,
      "shipped": 4,
      "delivered": 6,
    };
    const level = isCancelled ? 7 : (levels[s] || 1);

    if (isCancelled) {
      return [
        { id: 1, title: "Order Creation", desc: "Order is placed and recorded.", icon: "ri-shopping-bag-line", date: order?.created_at, completed: true },
        { id: 2, title: "Cancelled/Returned", desc: "Order was cancelled or returned.", icon: "mdi mdi-archive-remove-outline", date: order?.updated_at, completed: true, isError: true }
      ];
    }

    return [
      { id: 1, title: "Order Creation", desc: "Order is placed and recorded.", icon: "ri-shopping-bag-line", date: order?.created_at, completed: level >= 1 },
      { id: 2, title: "Order Confirmation", desc: "Payment and order details are verified.", icon: "ri-check-line", date: level >= 2 ? order?.updated_at : null, completed: level >= 2 },
      { id: 3, title: "Processing", desc: "Items are being prepared or packed.", icon: "mdi mdi-gift-outline", date: level >= 3 ? order?.updated_at : null, completed: level >= 3 },
      { id: 4, title: "Shipped/Dispatched", desc: "Order has left the warehouse.", icon: "ri-truck-line", date: level >= 4 ? order?.updated_at : null, completed: level >= 4 },
      { id: 5, title: "In Transit", desc: "Order is on the way to the customer.", icon: "ri-takeaway-fill", date: level >= 5 ? order?.updated_at : null, completed: level >= 5 },
      { id: 6, title: "Delivered", desc: "Customer has received the order.", icon: "mdi mdi-package-variant", date: level >= 6 ? order?.updated_at : null, completed: level >= 6 }
    ];
  };

  const steps = getOrderProgress(order?.order_status);

  // Initialize only completed steps to be open by default
  const [openSteps, setOpenSteps] = useState({});
  
  React.useEffect(() => {
    const initialOpen = {};
    steps.forEach(step => {
      initialOpen[step.id] = step.completed;
    });
    setOpenSteps(initialOpen);
  }, [order?.order_status]);

  const toggleStep = (id) => setOpenSteps(prev => ({ ...prev, [id]: !prev[id] }));

document.title ="Order Details -  Dashboard";

  const items = order && order.items ? (typeof order.items === 'string' ? JSON.parse(order.items) : order.items) : defaultProductDetails;
  const shippingAddress = order && order.shipping_address ? (typeof order.shipping_address === 'string' ? JSON.parse(order.shipping_address) : order.shipping_address) : {};
  return (
    <div className="page-content">
      <Container fluid>        
        <BreadCrumb title="Order Details" pageTitle="Dashboard" />

        <Row>
          <Col xl={9}>
            <Card>
              <CardHeader>
                <div className="d-flex align-items-center">
                  <h5 className="card-title flex-grow-1 mb-0">Order #{order?.order_number || "VL2667"}</h5>
                  <div className="flex-shrink-0">
                    <Link
                      to={`/apps-invoices-details/${order?._id || id}`}
                      className="btn btn-success btn-sm"
                    >
                      <i className="ri-download-2-fill align-middle me-1"></i>{" "}
                      Invoice
                    </Link>
                  </div>
                </div>
              </CardHeader>
              <CardBody>
                <div className="table-responsive table-card">
                  <table className="table table-nowrap align-middle table-borderless mb-0">
                    <thead className="table-light text-muted">
                      <tr>
                        <th scope="col">Product Details</th>
                        <th scope="col">Item Price</th>
                        <th scope="col">Quantity</th>
                        {/* <th scope="col">Rating</th> */}
                        <th scope="col" className="text-end">
                          Total Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((product, key) => (
                        <EcommerceOrderProduct product={{
                          img: product.img || product.image,
                          name: product.name,
                          color: product.color || "N/A",
                          size: product.size || "N/A",
                          price: product.price || "£0.00",
                          quantity: product.quantity,
                          amount: "£" + ((Number(String(product.price || 0).replace(/[^0-9.-]+/g,"")) || 0) * (product.quantity || 1)).toFixed(2)
                        }} key={key} />
                      ))}
                      <tr className="border-top border-top-dashed">
                        <td colSpan="3"></td>
                        <td colSpan="2" className="fw-medium p-0">
                          <table className="table table-borderless mb-0">
                            <tbody>
                              <tr>
                                <td>Sub Total :</td>
                                <td className="text-end">£{(Number(order?.total) || 359.96).toFixed(2)}</td>
                              </tr>
                              <tr>
                                <td>
                                  Discount{" "}
                                  <span className="text-muted">(COIN15)</span>{" "}
                                  : :
                                </td>
                                <td className="text-end">-£0.00</td>
                              </tr>
                              <tr>
                                <td>Shipping Charge :</td>
                                <td className="text-end">£0.00</td>
                              </tr>
                              <tr>
                                <td>Estimated Tax :</td>
                                <td className="text-end">£0.00</td>
                              </tr>
                              <tr className="border-top border-top-dashed">
                                <th scope="row">Total (GBP) :</th>
                                <th className="text-end">£{(Number(order?.total) || 415.96).toFixed(2)}</th>
                              </tr>
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <div className="d-sm-flex align-items-center">
                  <h5 className="card-title flex-grow-1 mb-0">Order Status</h5>
                  <div className="flex-shrink-0 mt-2 mt-sm-0">
                    {/* <Link
                      to="#"
                      className="btn btn-soft-info btn-sm mt-2 mt-sm-0"
                    >
                      <i className="ri-map-pin-line align-middle me-1"></i>{" "}
                      Change Address
                    </Link>{" "} */}
                    <Link
                      to="#"
                      className="btn btn-soft-danger btn-sm mt-2 mt-sm-0"
                    >
                      <i className="mdi mdi-archive-remove-outline align-middle me-1"></i>{" "}
                      Cancel Order
                    </Link>
                  </div>
                </div>
              </CardHeader>
              <CardBody>
                <div className="profile-timeline">
                  <div className="accordion accordion-flush" id="accordionFlushExample">
                    {steps.map((step) => (
                      <div className="accordion-item border-0" key={step.id} onClick={() => toggleStep(step.id)}>
                        <div className="accordion-header" id={`heading${step.id}`}>
                          <Link to="#" className={classnames("accordion-button", "p-2", "shadow-none", { collapsed: !openSteps[step.id] })}>
                            <div className="d-flex align-items-center">
                              <div className="flex-shrink-0 avatar-xs">
                                <div className={`avatar-title rounded-circle ${step.isError ? "bg-danger" : (step.completed ? "bg-success" : "bg-light text-success")}`}>
                                  <i className={step.icon}></i>
                                </div>
                              </div>
                              <div className="flex-grow-1 ms-3">
                                <h6 className="fs-15 mb-0 fw-semibold">
                                  {step.title}
                                  {step.date && <span className="fw-normal"> - {moment(step.date).format("ddd, DD MMM YYYY")}</span>}
                                </h6>
                              </div>
                            </div>
                          </Link>
                        </div>
                        <Collapse id={`collapse${step.id}`} className="accordion-collapse" isOpen={openSteps[step.id]}>
                          <div className="accordion-body ms-2 ps-5 pt-0">
                            <h6 className="mb-1">{step.desc}</h6>
                            {step.date && <p className="text-muted mb-0">{moment(step.date).format("ddd, DD MMM YYYY - hh:mmA")}</p>}
                          </div>
                        </Collapse>
                      </div>
                    ))}
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col xl={3}>
            <Card>
              <CardHeader>
                <div className="d-flex">
                  <h5 className="card-title flex-grow-1 mb-0">
                    <i className="mdi mdi-truck-fast-outline align-middle me-1 text-muted"></i>
                    Shipment Information
                  </h5>
                  <div className="flex-shrink-0">
                    <Button color="primary" size="sm" onClick={handleEditShipment}>
                      Edit Shipment
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardBody>
                <div className="text-center mb-3">
                  <lord-icon
                    src="https://cdn.lordicon.com/uetqnvvg.json"
                    trigger="loop"
                    colors="primary:#405189,secondary:#0ab39c"
                    style={{ width: "80px", height: "80px" }}
                  ></lord-icon>
                  <h5 className="fs-16 mt-2">{order?.courier_company || "Awaiting Shipment"}</h5>
                  <p className="text-muted mb-0">Tracking: {order?.tracking_number || "N/A"}</p>
                  {order?.tracking_url && (
                    <a href={order.tracking_url} target="_blank" rel="noopener noreferrer" className="text-primary mt-1 d-block">
                      Track Order Link
                    </a>
                  )}
                </div>
                {order?.shipment_date && (
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">Shipment Date:</span>
                    <span>{moment(order.shipment_date).format("DD MMM YYYY")}</span>
                  </div>
                )}
                {order?.estimated_delivery_date && (
                  <div className="d-flex justify-content-between">
                    <span className="text-muted">Est. Delivery:</span>
                    <span>{moment(order.estimated_delivery_date).format("DD MMM YYYY")}</span>
                  </div>
                )}
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <div className="d-flex">
                  <h5 className="card-title flex-grow-1 mb-0">
                    Customer Details
                  </h5>
                  <div className="flex-shrink-0">
                    <Link to="#" className="link-secondary">
                      View Profile
                    </Link>
                  </div>
                </div>
              </CardHeader>
              <CardBody>
                <ul className="list-unstyled mb-0 vstack gap-3">
                  <li>
                    <div className="d-flex align-items-center">
                      <div className="flex-shrink-0">
                        <img
                          src={avatar3}
                          alt=""
                          className="avatar-sm rounded"
                        />
                      </div>
                      <div className="flex-grow-1 ms-3">
                        <h6 className="fs-14 mb-1">{shippingAddress?.fullName || order?.email || "Joseph Parkers"}</h6>
                        <p className="text-muted mb-0">Customer</p>
                      </div>
                    </div>
                  </li>
                  <li>
                    <i className="ri-mail-line me-2 align-middle text-muted fs-16"></i>
                    {order?.email || "josephparker@gmail.com"}
                  </li>
                  <li>
                    <i className="ri-phone-line me-2 align-middle text-muted fs-16"></i>
                    {shippingAddress?.phone || "+(256) 245451 441"}
                  </li>
                </ul>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <h5 className="card-title mb-0">
                  <i className="ri-map-pin-line align-middle me-1 text-muted"></i>{" "}
                  Billing Address
                </h5>
              </CardHeader>
              <CardBody>
                <ul className="list-unstyled vstack gap-2 fs-13 mb-0">
                  <li className="fw-medium fs-14">{shippingAddress?.fullName || "Joseph Parker"}</li>
                  <li>{shippingAddress?.phone || "+(256) 245451 451"}</li>
                  <li>{shippingAddress?.addressLine1 || "2186 Joyce Street Rocky Mount"}</li>
                  <li>{shippingAddress?.state || "New York"} - {shippingAddress?.zipcode || "25645"}</li>
                  <li>{shippingAddress?.country || "United States"}</li>
                </ul>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <h5 className="card-title mb-0">
                  <i className="ri-map-pin-line align-middle me-1 text-muted"></i>{" "}
                  Shipping Address
                </h5>
              </CardHeader>
              <CardBody>
                <ul className="list-unstyled vstack gap-2 fs-13 mb-0">
                  <li className="fw-medium fs-14">{shippingAddress?.fullName || "Joseph Parker"}</li>
                  <li>{shippingAddress?.phone || "+(256) 245451 451"}</li>
                  <li>{shippingAddress?.addressLine1 || "2186 Joyce Street Rocky Mount"}</li>
                  <li>{shippingAddress?.state || "California"} - {shippingAddress?.zipcode || "24567"}</li>
                  <li>{shippingAddress?.country || "United States"}</li>
                </ul>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <h5 className="card-title mb-0">
                  <i className="ri-secure-payment-line align-bottom me-1 text-muted"></i>{" "}
                  Payment Details
                </h5>
              </CardHeader>
              <CardBody>
                <div className="d-flex align-items-center mb-2">
                  <div className="flex-shrink-0">
                    <p className="text-muted mb-0">Transactions:</p>
                  </div>
                  <div className="flex-grow-1 ms-2">
                    <h6 className="mb-0">#{order?.order_number || "VLZ124561278124"}</h6>
                  </div>
                </div>
                <div className="d-flex align-items-center mb-2">
                  <div className="flex-shrink-0">
                    <p className="text-muted mb-0">Payment Method:</p>
                  </div>
                  <div className="flex-grow-1 ms-2">
                    <h6 className="mb-0">{order?.payment_method === 'cod' ? 'Cash on Delivery' : (order?.payment_method || 'Debit Card')}</h6>
                  </div>
                </div>
                <div className="d-flex align-items-center mb-2">
                  <div className="flex-shrink-0">
                    <p className="text-muted mb-0">Card Holder Name:</p>
                  </div>
                  <div className="flex-grow-1 ms-2">
                    <h6 className="mb-0">Joseph Parker</h6>
                  </div>
                </div>
                <div className="d-flex align-items-center mb-2">
                  <div className="flex-shrink-0">
                    <p className="text-muted mb-0">Card Number:</p>
                  </div>
                  <div className="flex-grow-1 ms-2">
                    <h6 className="mb-0">xxxx xxxx xxxx 2456</h6>
                  </div>
                </div>
                <div className="d-flex align-items-center">
                  <div className="flex-shrink-0">
                    <p className="text-muted mb-0">Total Amount:</p>
                  </div>
                  <div className="flex-grow-1 ms-2">
                    <h6 className="mb-0">£{(Number(order?.total) || 415.96).toFixed(2)}</h6>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Edit Shipment Modal */}
        <Modal isOpen={modal} toggle={toggleModal} centered>
          <ModalHeader toggle={toggleModal}>Edit Shipment Information</ModalHeader>
          <Form onSubmit={handleShipmentSubmit}>
            <ModalBody>
              <FormGroup>
                <Label for="courier_company">Courier Company <span className="text-danger">*</span></Label>
                <Input
                  type="select"
                  id="courier_company"
                  value={shipmentForm.courier_company}
                  onChange={(e) => setShipmentForm({ ...shipmentForm, courier_company: e.target.value })}
                  required
                >
                  <option value="">Select Courier</option>
                  <option value="FedEx">FedEx</option>
                  <option value="UPS">UPS</option>
                  <option value="DHL">DHL</option>
                  <option value="USPS">USPS</option>
                  <option value="Royal Mail">Royal Mail</option>
                  <option value="Other">Other</option>
                </Input>
              </FormGroup>
              <FormGroup>
                <Label for="tracking_number">Tracking Number <span className="text-danger">*</span></Label>
                <Input
                  type="text"
                  id="tracking_number"
                  placeholder="Enter tracking number"
                  value={shipmentForm.tracking_number}
                  onChange={(e) => setShipmentForm({ ...shipmentForm, tracking_number: e.target.value })}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label for="tracking_url">Tracking URL (Optional)</Label>
                <Input
                  type="url"
                  id="tracking_url"
                  placeholder="https://example.com/track"
                  value={shipmentForm.tracking_url}
                  onChange={(e) => setShipmentForm({ ...shipmentForm, tracking_url: e.target.value })}
                />
              </FormGroup>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label for="shipment_date">Shipment Date</Label>
                    <Input
                      type="date"
                      id="shipment_date"
                      value={shipmentForm.shipment_date}
                      onChange={(e) => setShipmentForm({ ...shipmentForm, shipment_date: e.target.value })}
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label for="estimated_delivery_date">Est. Delivery Date</Label>
                    <Input
                      type="date"
                      id="estimated_delivery_date"
                      value={shipmentForm.estimated_delivery_date}
                      onChange={(e) => setShipmentForm({ ...shipmentForm, estimated_delivery_date: e.target.value })}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <FormGroup check className="mt-2">
                <Label check>
                  <Input
                    type="checkbox"
                    checked={shipmentForm.notifyCustomer}
                    onChange={(e) => setShipmentForm({ ...shipmentForm, notifyCustomer: e.target.checked })}
                  />{" "}
                  Notify customer via Email/SMS/WhatsApp
                </Label>
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={toggleModal}>Cancel</Button>
              <Button color="primary" type="submit" disabled={loading}>
                {loading ? "Saving..." : "Save Changes"}
              </Button>
            </ModalFooter>
          </Form>
        </Modal>

        <ToastContainer position="top-right" autoClose={3000} />
      </Container>
    </div>
  );
};

export default EcommerceOrderDetail;